import TicketIcon from "../../../shared-components/icons/TicketIcon";
import ArrowRightIcon from "../../../shared-components/icons/ArrowRightIcon";
import StatisticIcon from "../../../shared-components/icons/StatisticIcon";
import GiftIcon from "../../../shared-components/icons/GiftIcon";
import { DEVICE_RESOLUTION } from "@/app/app-constants";
import { useEffect, useState } from "react";
import { useBotbLeaderboardContext } from "../context";
import { useTimer } from "@/app/hooks/useTimer";
import Skeleton from "react-loading-skeleton";

type StepProps = {
  number: number;
  icon: React.ElementType;
  text: React.ReactNode;
};

const Step = ({ number, icon: Icon, text }: StepProps) => {
  return (
    <div className="pl-2.5 lg:pl-2 relative">
      <div className="absolute left-0 lg:left-0 -top-1 lg:-top-2 flex justify-center items-center aspect-[1/1] bg-[#E87600] size-4.5 lg:size-6 rounded-full">
        <span className="text-white text-center font-jost text-xs font-semibold leading-4 lg:text-base lg:leading-[22px]">
          {number}
        </span>
      </div>
      <div className="flex items-center gap-1.5 lg:gap-2.5 text-primary">
        <Icon className="size-6 lg:size-10 shrink-0" />
        <div className="text-[#696971] font-jost text-xs font-medium leading-3 lg:text-xl lg:leading-[22px]">
          {text}
        </div>
      </div>
    </div>
  );
};

const MainBanner = () => {
  const { tournamentEndDate, tournamentFinished, isLeaderboardDataLoading } = useBotbLeaderboardContext();
  const timer = useTimer("MainBanner", tournamentEndDate, undefined, "millisecond");
  const [isMobile, setIsMobile] = useState(
    window.innerWidth >= DEVICE_RESOLUTION.MobileMin && window.innerWidth < DEVICE_RESOLUTION.Laptop
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth >= DEVICE_RESOLUTION.MobileMin && window.innerWidth < DEVICE_RESOLUTION.Laptop);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isLeaderboardDataLoading) {
    return (
      <div className="w-full min-h-[348px] lg:min-h-[440px] flex flex-col items-center justify-center bg-white">
        <Skeleton
          highlightColor="#fff"
          count={1}
          inline
          height={isMobile ? 348 : 440}
          containerClassName="w-full"
          className="!rounded-none"
        />
      </div>
    );
  }

  return (
    <div
      className={`w-full min-h-[348px] lg:min-h-[440px] flex flex-col max-lg:justify-end items-center shrink-0 bg-white px-2 lg:pt-[80px] lg:pb-[66px] bg-no-repeat bg-100% lg:bg-cover ${tournamentFinished ? "pt-[198px] pb-3 lg:justify-center" : "pt-[128px] pb-6"}`}
      style={{
        backgroundImage: isMobile
          ? "url(/assets/images/leaderboard/main-banner-m.webp)"
          : "url(/assets/images/leaderboard/main-banner-d.webp)",
      }}>
      <div className={"flex flex-col items-center lg:items-start gap-5 lg:gap-8 w-full max-w-[1232px] lg:pl-[100px]"}>
        <div className={"flex flex-col items-center gap-4 lg:max-w-[587px]"}>
          <div className={"flex items-center justify-center lg:justify-start gap-2 self-stretch"}>
            {/*<div className={"font-jost text-[13px] font-semibold leading-[normal] bg-gradient-botb bg-clip-text [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] whitespace-nowrap"}>*/}
            {/*    BLACK FRIDAY LEADERBOARD*/}
            {/*</div>*/}
            {!timer.finished ? (
              <div
                className={
                  "flex min-h-5 justify-center lg:justify-start items-center gap-2.5 rounded border border-black/8 bg-gradient-botb px-1.5 py-0.5 border-solid lg:min-h-6 lg:px-2"
                }>
                <div
                  className={
                    "flex gap-1 text-white font-jost text-[11px] font-medium leading-[14px] lg:text-sm lg:font-semibold lg:leading-5"
                  }>
                  {!timer.finished ? (
                    <>
                      <div>ENDS IN</div>
                      <div>
                        {timer.isPastADay ? (
                          <div>
                            {timer.days} {timer.days === "01" ? "DAY" : "DAYS"}
                          </div>
                        ) : (
                          <div className={"flex"}>
                            <div>{timer.hours}:</div>
                            <div>{timer.minutes}:</div>
                            <div>
                              <span className={"min-w-3.5 lg:min-w-[18px] inline-flex text-right"}>
                                {timer.seconds}
                              </span>
                              <span className={"text-[9px] lg:text-[11px] min-w-3 lg:min-w-4 inline-flex"}>
                                {Math.floor(timer.milliseconds / 10)
                                  .toString()
                                  .padStart(2, "0")}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>EVERYBODY WINS LEADERBOARD: CLOSED</>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <div className={"flex flex-col justify-center lg:justify-start items-center gap-3 lg:gap-2 self-stretch"}>
            {timer.finished ? (
              <div
                className={
                  "self-center lg:self-start inline-flex items-center justify-center px-2 py-0.5 rounded border border-black/8 border-solid bg-[#f33424]"
                }>
                <span
                  className={
                    "font-jost text-[11px] font-medium leading-[14px] lg:text-sm lg:font-semibold lg:leading-5 text-white whitespace-nowrap"
                  }>
                  EVERYBODY WINS LEADERBOARD: CLOSED
                </span>
              </div>
            ) : null}
            <div
              className={
                "self-stretch text-white text-center lg:text-left font-jost text-2xl font-bold leading-[30px] lg:text-[40px] lg:leading-[48px] bg-gradient-botb bg-clip-text [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]"
              }>
              {!timer.finished ? "The £300,000 Everybody Wins Summer Leaderboard" : "The Results Are In!"}
            </div>
            <div
              className={`self-stretch text-[#28282B] text-center lg:text-left font-jost text-sm font-medium leading-5 lg:text-xl lg:font-normal lg:leading-8 ${tournamentFinished ? "max-lg:max-w-[307px] max-lg:mx-auto" : ""}`}>
              {!timer.finished ? (
                <>Over 200,000 players are guaranteed to win a prize! Just score 200 points to qualify.</>
              ) : (
                <>
                  Congratulations to our 200,000 winners! Thanks for
                  <br className={"max-lg:hidden"} /> playing, and we'll see you at the next Leaderboard.
                </>
              )}
            </div>
          </div>
        </div>

        <div
          className={`flex items-center justify-center lg:justify-start gap-1 lg:gap-[29px] self-stretch pt-1 lg:pt-2 ${!timer.finished ? "" : "hidden"}`}>
          <Step
            number={1}
            icon={TicketIcon}
            text={
              <>
                Enter <br />
                competitions
              </>
            }
          />
          <ArrowRightIcon variant={3} className={"-mt-1 size-3 lg:size-4 shrink-0 text-black/20"} />
          <Step
            number={2}
            icon={StatisticIcon}
            text={
              <>
                Climb the <br /> ladder
              </>
            }
          />
          <ArrowRightIcon variant={3} className={"-mt-1 size-3 lg:size-4 shrink-0 text-white/38"} />
          <Step
            number={3}
            icon={GiftIcon}
            text={
              <>
                Win <br />
                Prizes
              </>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MainBanner;
